import { ChartConfiguration, Scale, TooltipItem } from 'chart.js';
import {
  PartnerScorecard,
  PartnerScorecardDataVm,
  SCORECARD_SERIES,
  ScorecardChart,
  ScorecardChartVm,
  ScorecardDeclinedChartVm,
  ScorecardMonth,
  ScorecardSeries,
} from '@partner/feature-modules/dashboard/domain/model/partner-scorecard.model';
import { formatCompactCurrency } from './compact-currency.helper';
import { formatCompanyDateTime } from './dashboard-filters.helper';
import { BAR_LABEL_EDGES, NO_VALUE_LABEL, percentLabel } from './pipeline-funnel.helper';

export interface ScorecardChartCopy {
  title: string;
  subtitle: string;
}

/**
 * Figma 15308:9805 copy; the approval-rate subtitle is corrected, the booking card follows the ticket, and the funded
 * subtitles name the first draw because Home's "Activation draws" is a different figure.
 */
export const SCORECARD_CHARTS: Record<ScorecardChart, ScorecardChartCopy> = {
  [ScorecardChart.FUNDED_AMOUNT]: {
    title: 'Partner funded amount over time',
    subtitle: 'First-draw amount by first-draw date',
  },
  [ScorecardChart.FUNDED_UNITS]: {
    title: 'Partner funded units over time',
    subtitle: 'Funded loans by first-draw date',
  },
  [ScorecardChart.SUBMITTED]: {
    title: 'Partner total submissions over time',
    subtitle: 'Submission count by created date',
  },
  [ScorecardChart.APPROVED]: {
    title: 'Partner total approved submissions',
    subtitle: 'Approved submission count by created date',
  },
  [ScorecardChart.APPROVAL_RATE]: {
    title: 'Partner approval rate',
    subtitle: 'approved ÷ submitted, by created date, in %',
  },
  [ScorecardChart.BOOKING_RATE]: {
    title: 'Partner booking rate',
    subtitle: 'funded ÷ approved, by created date, in %',
  },
  [ScorecardChart.DECLINED_REASONS]: {
    title: 'Declined reasons',
    subtitle: 'Record count by declined reason, all time',
  },
};

export const DECLINED_EMPTY_MESSAGE = 'No declined submissions with a reason.';

/** The reason tick box (84 px in Figma) holds this many characters; a longer reason is cut and ellipsised. */
export const MAX_REASON_TICK_CHARS = 16;

/**
 * The reason column (84 px in Figma) plus its 12 px gap to the bars: the floor the reason axis fits to. The tick mark
 * (8 px) and padding (3 px) leave 85 px of it for text; a wider label widens the axis.
 */
export const REASON_AXIS_MIN_WIDTH_PX = 96;

/** The category axis on the declined card's right that carries each bar's count. */
export const DECLINED_COUNT_AXIS_ID = 'yCount';

const ELLIPSIS = '…';
const NO_DATA_ARIA_LABEL = 'no data';

export enum ScorecardDeclinedTier {
  TOP = 'top',
  TIER_2_4 = 'tier-2-4',
  TIER_5_6 = 'tier-5-6',
  TIER_7_11 = 'tier-7-11',
  TIER_12_15 = 'tier-12-15',
}

export interface ScorecardColors {
  best: string;
  other: string;
  tiers: Record<ScorecardDeclinedTier, string>;
  monthTick: string;
  reasonTick: string;
  countTick: string;
}

// Chart-semantic fills and inks from Figma 15308:9805. Declared here because the canvas cannot read SCSS;
// `_scorecard-palette.scss` mirrors them and a spec pins the two equal. Best and tier 2-4 are status(success), top is
// status(warning); the neutrals carry no style in the design.
export const SCORECARD_COLORS: ScorecardColors = {
  best: '#006E28',
  other: '#647E63',
  tiers: {
    [ScorecardDeclinedTier.TOP]: '#E28F2A',
    [ScorecardDeclinedTier.TIER_2_4]: '#006E28',
    [ScorecardDeclinedTier.TIER_5_6]: '#647E63',
    [ScorecardDeclinedTier.TIER_7_11]: '#B2CEAF',
    [ScorecardDeclinedTier.TIER_12_15]: '#DEE5D9',
  },
  monthTick: 'rgba(0, 0, 0, 0.54)',
  reasonTick: '#555F52',
  countTick: '#555F52',
};

const FONT_FAMILY = '"Roboto", sans-serif';
export const SCORECARD_MONTH_TICK_FONT = { family: FONT_FAMILY, size: 11, weight: 500 } as const;
export const SCORECARD_REASON_TICK_FONT = { family: FONT_FAMILY, size: 10, weight: 500 } as const;
export const SCORECARD_COUNT_TICK_FONT = { family: FONT_FAMILY, size: 12, weight: 400 } as const;

// Figma bar geometry: month bars 143 px in a 155 px slot; declined bars 24 px at a 32 px pitch.
const MONTH_BAR_PERCENTAGE = 143 / 155;
const DECLINED_BAR_PERCENTAGE = 24 / 32;

const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_PATTERN = /^\d{2}(\d{2})-(\d{2})$/;

/** `2025-03` → `Mar 25`, read from the string alone; anything else is returned as given. */
export function monthLabel(month: string): string {
  const match = MONTH_PATTERN.exec(month);
  if (!match) {
    return month;
  }
  const abbreviation = MONTH_ABBREVIATIONS[Number(match[2]) - 1];
  return abbreviation ? `${abbreviation} ${match[1]}` : month;
}

function monthCount(count: number): string {
  return count === 1 ? '1 month' : `${count} months`;
}

function isRate(series: ScorecardSeries): boolean {
  return series === ScorecardChart.APPROVAL_RATE || series === ScorecardChart.BOOKING_RATE;
}

/** The bar's value: the percent for a rate (null with no denominator), the figure otherwise. */
export function seriesValue(month: ScorecardMonth, series: ScorecardSeries): number | null {
  switch (series) {
    case ScorecardChart.APPROVAL_RATE:
      return month.approvalRate.percent;
    case ScorecardChart.BOOKING_RATE:
      return month.bookingRate.percent;
    default:
      return month[series];
  }
}

/** Compact currency for the amount, the integer for a count, the bar-edge percent for a rate; `—` for no value. */
export function valueLabel(series: ScorecardSeries, value: number | null): string {
  if (value === null) {
    return NO_VALUE_LABEL;
  }
  if (series === ScorecardChart.FUNDED_AMOUNT) {
    return formatCompactCurrency(value);
  }
  return isRate(series) ? percentLabel(value, BAR_LABEL_EDGES) : `${value}`;
}

/** `Trailing 19 months · as of Sep 28, 2026 3:16 PM`, N = the months on the wire. */
export function footerLabel(months: readonly ScorecardMonth[], asOfUtc: Date): string {
  return `Trailing ${monthCount(months.length)} · as of ${formatCompanyDateTime(asOfUtc)}`;
}

/** `All time · as of Sep 28, 2026 3:16 PM`. */
export function declinedFooterLabel(asOfUtc: Date): string {
  return `All time · as of ${formatCompanyDateTime(asOfUtc)}`;
}

/**
 * One line per card, true whether its months are null (no denominator) or a real 0. Booking rate names its
 * created-date cohort: the funded cards are dated by first draw, so they can chart beside it.
 */
export function emptyMessage(series: ScorecardSeries, monthsShown: number): string {
  const months = monthCount(monthsShown);
  switch (series) {
    case ScorecardChart.FUNDED_AMOUNT:
    case ScorecardChart.FUNDED_UNITS:
      return `Nothing funded in the last ${months}.`;
    case ScorecardChart.SUBMITTED:
      return `No submissions in the last ${months}.`;
    case ScorecardChart.APPROVED:
    case ScorecardChart.APPROVAL_RATE:
      return `No approvals in the last ${months}.`;
    case ScorecardChart.BOOKING_RATE:
      return `No approved submission created in the last ${months} has funded yet.`;
  }
}

/** The colour tier of a competition rank: tied reasons share one, whatever their position. */
export function declinedTier(rank: number): ScorecardDeclinedTier {
  if (rank <= 1) {
    return ScorecardDeclinedTier.TOP;
  }
  if (rank <= 4) {
    return ScorecardDeclinedTier.TIER_2_4;
  }
  if (rank <= 6) {
    return ScorecardDeclinedTier.TIER_5_6;
  }
  return rank <= 11 ? ScorecardDeclinedTier.TIER_7_11 : ScorecardDeclinedTier.TIER_12_15;
}

/** `Below Required Bank Balance` → `Below Required…`; a reason of 16 characters or fewer shows whole. */
export function reasonTickLabel(reason: string): string {
  if (reason.length <= MAX_REASON_TICK_CHARS) {
    return reason;
  }
  return `${reason.slice(0, MAX_REASON_TICK_CHARS - 1).trimEnd()}${ELLIPSIS}`;
}

function monthChartOptions(series: ScorecardSeries): ChartConfiguration<'bar'>['options'] {
  return {
    responsive: true,
    maintainAspectRatio: false,
    // The whole month column opens its tooltip: a 0 bar has no height and a null month no bar to hit.
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (item: TooltipItem<'bar'>): string => valueLabel(series, item.parsed.y) } },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { autoSkip: false, color: SCORECARD_COLORS.monthTick, font: { ...SCORECARD_MONTH_TICK_FONT } },
      },
      y: { display: false, beginAtZero: true },
    },
  };
}

function monthAriaLabel(
  title: string,
  labels: readonly string[],
  series: ScorecardSeries,
  values: (number | null)[],
): string {
  const points = labels.map(
    (label, index) => `${label} ${values[index] === null ? NO_DATA_ARIA_LABEL : valueLabel(series, values[index])}`,
  );
  return `${title}: ${points.join(', ')}`;
}

/** One month card: `best` from the server is the only bar painted, a null rate draws no bar. */
export function toScorecardChartVm(scorecard: PartnerScorecard, series: ScorecardSeries): ScorecardChartVm {
  const { title, subtitle } = SCORECARD_CHARTS[series];
  const labels = scorecard.months.map((month) => monthLabel(month.month));
  const values = scorecard.months.map((month) => seriesValue(month, series));
  const best = scorecard.best[series];
  const colors = values.map((_, index) => (index === best ? SCORECARD_COLORS.best : SCORECARD_COLORS.other));
  return {
    chart: series,
    title,
    subtitle,
    data: {
      labels,
      datasets: [
        {
          label: title,
          data: values,
          backgroundColor: colors,
          hoverBackgroundColor: colors,
          categoryPercentage: 1,
          barPercentage: MONTH_BAR_PERCENTAGE,
        },
      ],
    },
    options: monthChartOptions(series),
    ariaLabel: monthAriaLabel(title, labels, series, values),
    isEmpty: values.every((value) => value === null || value === 0),
    emptyMessage: emptyMessage(series, scorecard.months.length),
    footerLabel: footerLabel(scorecard.months, scorecard.asOfUtc),
    link: scorecard.links[series],
  };
}

/** The six month cards, in `SCORECARD_SERIES` order. */
export function toScorecardChartVms(scorecard: PartnerScorecard): ScorecardChartVm[] {
  return SCORECARD_SERIES.map((series) => toScorecardChartVm(scorecard, series));
}

function declinedChartOptions(
  reasons: readonly string[],
  counts: readonly string[],
): ChartConfiguration<'bar'>['options'] {
  return {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    // The whole row opens its tooltip, the only place a cut reason reads whole: a count of 1 is a sliver on a phone.
    // Index mode measures along x unless told otherwise, which on horizontal bars picks a bar by its length.
    interaction: { mode: 'index', axis: 'y', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: TooltipItem<'bar'>[]): string => (items.length ? reasons[items[0].dataIndex] : ''),
          label: (item: TooltipItem<'bar'>): string => counts[item.dataIndex],
        },
      },
    },
    scales: {
      x: { display: false, beginAtZero: true },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          autoSkip: false,
          color: SCORECARD_COLORS.reasonTick,
          font: { ...SCORECARD_REASON_TICK_FONT },
          callback: (_value: string | number, index: number): string => reasonTickLabel(reasons[index]),
        },
        // With two side axes Chart.js caps each at a quarter of the canvas, which on a phone clips the widest reason;
        // raising the cap to half lets the axis fit its labels whole.
        beforeFit: (scale: Scale): void => {
          scale.maxWidth = Math.max(scale.maxWidth, scale.chart.width / 2);
        },
        afterFit: (scale: Scale): void => {
          scale.width = Math.max(scale.width, REASON_AXIS_MIN_WIDTH_PX);
        },
      },
      [DECLINED_COUNT_AXIS_ID]: {
        type: 'category',
        axis: 'y',
        position: 'right',
        offset: true,
        labels: [...counts],
        grid: { display: false },
        border: { display: false },
        ticks: { autoSkip: false, color: SCORECARD_COLORS.countTick, font: { ...SCORECARD_COUNT_TICK_FONT } },
      },
    },
  };
}

/** Horizontal bars by rank colour, full reasons as categories (ticks cut them), counts on the right-hand axis. */
export function toDeclinedChartVm(scorecard: PartnerScorecard): ScorecardDeclinedChartVm {
  const { title, subtitle } = SCORECARD_CHARTS[ScorecardChart.DECLINED_REASONS];
  const { reasons, remainingReasons } = scorecard.declinedReasons;
  const labels = reasons.map((reason) => reason.reason);
  const counts = reasons.map((reason) => `${reason.count}`);
  const colors = reasons.map((reason) => SCORECARD_COLORS.tiers[declinedTier(reason.rank)]);
  const moreLabel = remainingReasons > 0 ? `+${remainingReasons} more` : null;
  const points = reasons.map((reason) => `${reason.reason} ${reason.count}`);
  return {
    chart: ScorecardChart.DECLINED_REASONS,
    title,
    subtitle,
    data: {
      labels,
      datasets: [
        {
          label: title,
          data: reasons.map((reason) => reason.count),
          backgroundColor: colors,
          hoverBackgroundColor: colors,
          categoryPercentage: 1,
          barPercentage: DECLINED_BAR_PERCENTAGE,
        },
      ],
    },
    options: declinedChartOptions(labels, counts),
    ariaLabel: `${title}: ${[...points, ...(moreLabel ? [moreLabel] : [])].join(', ')}`,
    isEmpty: reasons.length === 0,
    emptyMessage: DECLINED_EMPTY_MESSAGE,
    footerLabel: declinedFooterLabel(scorecard.asOfUtc),
    link: scorecard.links[ScorecardChart.DECLINED_REASONS],
    moreLabel,
  };
}

export function toPartnerScorecardDataVm(scorecard: PartnerScorecard): PartnerScorecardDataVm {
  return { charts: toScorecardChartVms(scorecard), declined: toDeclinedChartVm(scorecard) };
}
